import React, { useMemo, useState } from 'react'
import DatePicker from 'react-widgets/DatePicker'
import { DateLocalizer } from 'react-widgets/IntlLocalizer'
import Localization from 'react-widgets/Localization'
import { useTypedTranslation } from 'src/shared/i18next/hooks/use-typed-translation'
import 'react-widgets/styles.css'
import { DatePickerUi } from './ui/date-picker-ui'

export interface DatePickerControlProps {
  dateTime: Date
  updateDate: (date: Date) => void
}

export const DatePickerControl = ({
  dateTime,
  updateDate,
}: DatePickerControlProps) => {
  const { i18n } = useTypedTranslation()
  const fallbackLng = i18n.options.fallbackLng
    ? (i18n.options.fallbackLng as string[])[0]
    : 'en'
  const language = i18n.language || fallbackLng
  const [currentCalendarDate, setCurrentCalendarDate] = useState(dateTime)

  const localizer = useMemo(
    () =>
      new DateLocalizer({
        culture: language,
        firstOfWeek: 1,
      }),
    [language],
  )

  return (
    <Localization date={localizer}>
      <DatePickerUi>
        <DatePicker
          containerClassName="[&>.rw-input]:h-10!"
          lang={language}
          currentDate={currentCalendarDate}
          calendarProps={{
            view: 'month',
            formats: {
              weekday: (date: Date) => {
                const short = date.toLocaleDateString(language, {
                  weekday: 'short',
                })

                return short.slice(0, 2)
              },
            },
            renderDay: ({ label, ...props }) => {
              const isToday =
                props.date.toDateString() === new Date().toDateString() &&
                props.date.toDateString() !== new Date(dateTime).toDateString()

              return (
                <span
                  {...props}
                  className={isToday ? 'text-[var(--color-primary-500)]' : ''}
                >
                  {label}
                </span>
              )
            },
            onNavigate: date => {
              setCurrentCalendarDate(date)
            },
          }}
          defaultValue={dateTime}
          onToggle={isOpen => {
            if (isOpen) {
              setCurrentCalendarDate(dateTime)
            }
          }}
          onChange={date => {
            if (date) {
              const nextDate = new Date(date)
              nextDate.setHours(
                dateTime.getHours(),
                dateTime.getMinutes(),
                dateTime.getSeconds(),
                dateTime.getMilliseconds(),
              )
              updateDate(nextDate)
              setCurrentCalendarDate(nextDate)
            }
          }}
        />
      </DatePickerUi>
    </Localization>
  )
}
